import React, { useEffect } from 'react';
import cn from 'classnames';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow as ShadcnTableRow } from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronRightIcon, ChevronDownIcon, TrashIcon, CheckCircleIcon, PlusIcon, DocumentDuplicateIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { ColumnConfig, TableRow } from '../types';
import { MOBILE_ACTIONS_COLUMN_WIDTH } from '../constants/tableLayout';

interface PredictionsTableRowProps {
  row: TableRow;
  rowIndex: number;
  columns: ColumnConfig[];
  collapsedRows: Set<number>;
  onCellChange: (rowIndex: number, columnKey: string, value: string | any[]) => void;
  onDeleteRow?: (rowIndex: number) => void;
  onDuplicateRow?: (rowIndex: number) => void;
  ontoggleRowExpand: (rowIndex: number) => void;
  onCellFocus: (rowIndex: number, columnKey: string) => void;
  onInsertRow?: (index: number) => void;
  showInsertColumn?: boolean;
  isValid?: boolean;
  isResolved?: boolean;
  highlightVariant?: 'amber' | 'green' | null;
  /**
   * Phone layout: replaces the hover-only insert button and floating action
   * panel with a sticky row-number cell that opens an actions menu.
   */
  isMobile?: boolean;
}

const PredictionsTableRow: React.FC<PredictionsTableRowProps> = ({
  row,
  rowIndex,
  columns,
  collapsedRows,
  onInsertRow,
  showInsertColumn = Boolean(onInsertRow),
  onCellChange,
  onDeleteRow,
  onDuplicateRow,
  ontoggleRowExpand,
  onCellFocus,
  isValid = true,
  isResolved = false,
  highlightVariant = null,
  isMobile = false,
}) => {
  const isExpanded = !collapsedRows.has(rowIndex);

  // Heading of the phone actions menu, so the user knows which row they are
  // acting on while the identifying columns are scrolled out of view.
  const rowLabel =
    (typeof row.title === 'string' && row.title.trim()) ||
    (typeof row.internal_id === 'string' && row.internal_id.trim()) ||
    `Row ${rowIndex + 1}`;

  const autoResize = (textarea: HTMLTextAreaElement) => {
    if (isExpanded) {
      textarea.style.height = '2.5rem';
      const scrollHeight = textarea.scrollHeight;
      const defaultHeight = 40;
      if (scrollHeight > defaultHeight) {
        textarea.style.height = `${scrollHeight}px`;
      }
    } else {
      textarea.style.height = '2.5rem';
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const textareas = document.querySelectorAll(`[data-row-index="${rowIndex}"] textarea`);
      textareas.forEach((textarea) => {
        if (textarea instanceof HTMLTextAreaElement) {
          autoResize(textarea);
        }
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isExpanded, rowIndex, row]);

  const renderCell = (column: ColumnConfig, row: TableRow, rowIndex: number): React.ReactNode => {
    const value = row[column.key] || '';
    const isExpanded = !collapsedRows.has(rowIndex);
    const cellProps = {
      value,
      onChange: (newValue: string | any[]) => onCellChange(rowIndex, column.key, newValue),
      rowIndex,
      column: column,
      row,
      isExpanded
    };

    switch (column.cell.type) {
      case 'custom':
        if (column.cell.component) {
          const CustomComponent = column.cell.component;
          const isInvalid = typeof column.cell.invalid === 'function'
            ? column.cell.invalid(rowIndex)
            : column.cell.invalid;
          return <CustomComponent {...cellProps} {...(column.cell.props || {})} invalid={isInvalid} />;
        }
        return value;

      case 'readonly':
        const formattedValue = column.formatter ? column.formatter(value) : value;

        if (column.key === 'internal_id') {
          return (
            <div className="tw-p-2" style={{ ...column.cell.style }}>
              {formattedValue}
            </div>
          );
        }

        const getTruncationLength = () => {
          if (column.key === 'plot') return 80;
          if (column.key === 'title') return 30;
          return 50;
        };

        const truncationLength = getTruncationLength();
        const shouldTruncate = !isExpanded && typeof formattedValue === 'string' && formattedValue.length > truncationLength;
        const displayValue = shouldTruncate
          ? `${formattedValue.substring(0, truncationLength)}...`
          : formattedValue;

        return (
          <div
            className="tw-p-2 tw-overflow-hidden tw-text-ellipsis"
            style={{ ...column.cell.style, whiteSpace: isExpanded ? 'normal' : 'nowrap' }}
            title={shouldTruncate ? formattedValue : undefined}
          >
            {displayValue}
          </div>
        );

      case 'editable':
      default:
        const isInvalid = typeof column.cell.invalid === 'function'
          ? column.cell.invalid(rowIndex)
          : column.cell.invalid;
        const isNumeric = column.cell.inputType === 'number';

        return (
          <Textarea
            ref={(el) => { if (el) autoResize(el); }}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const raw = e.target.value;
              const next = isNumeric ? raw.replace(/\D/g, '') : raw;
              onCellChange(rowIndex, column.key, next);
              autoResize(e.target);
            }}
            onFocus={() => onCellFocus(rowIndex, column.key)}
            disabled={column.cell.editable === false}
            inputMode={isNumeric ? 'numeric' : undefined}
            className={`tw-text-sm tw-w-full tw-h-[2.5rem] tw-min-h-[2.5rem] tw-align-top tw-resize-none tw-overflow-hidden ${isInvalid ? 'tw-border-red-500' : ''}`}
            style={{ ...column.cell.style }}
          />
        );
    }
  };

  const handleCellClick = (e: React.MouseEvent<HTMLTableCellElement>, column: ColumnConfig) => {
    // Popover-based editors (date picker, month/year selects) render through a
    // portal, so their clicks bubble here through the React tree while their
    // DOM lives outside the cell. On touch those editors open on click, and
    // treating that click as a cell click refocuses the cell and closes them.
    if (!e.currentTarget.contains(e.target as Node)) return;

    onCellFocus(rowIndex, column.key);

    if (column.cell.type === 'editable' && column.cell.editable !== false) {
      const textarea = e.currentTarget.querySelector('textarea') as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        textarea.select();
      }
    } else if (column.cell.type === 'custom') {
      const clickableElement = e.currentTarget.querySelector(
        '.person-inline-view, .person-expanded-view, .genre-inline-view, ' +
        '.multi-select-dropdown__inline-view, .multi-select-dropdown__expanded-view'
      ) as HTMLElement;

      if (clickableElement) {
        clickableElement.click();
      } else {
        const multiSelectContainer = e.currentTarget.querySelector('[data-component-type="multi-select"]') as HTMLElement;
        if (multiSelectContainer) {
          const customEvent = new CustomEvent('cellClick', { bubbles: true });
          multiSelectContainer.dispatchEvent(customEvent);
        } else {
          const selectControl = e.currentTarget.querySelector('.react-select__control') as HTMLElement;
          if (selectControl) {
            selectControl.click();
          } else {
            const focusableElement = e.currentTarget.querySelector('textarea, input, select, [tabindex]:not([tabindex="-1"])') as HTMLElement;
            if (focusableElement) {
              focusableElement.focus();
            }
          }
        }
      }
    }
  };

  return (
    <ShadcnTableRow
      data-row-index={rowIndex}
      className={cn(
        'tw-group tw-transition-colors tw-duration-300',
        highlightVariant === 'amber' && 'tw-bg-amber-100/60 dark:tw-bg-amber-900/30',
        highlightVariant === 'green' && 'tw-bg-green-200/60 dark:tw-bg-green-900/30',
        !highlightVariant && isResolved && 'tw-border-l-2 tw-border-l-green-500 tw-bg-green-50 dark:tw-bg-green-950/20',
        !highlightVariant && !isResolved && !isValid && 'tw-border-l-2 tw-border-l-red-500',
      )}
      style={{ height: 'auto', maxHeight: isExpanded ? '300px' : 'auto' }}
      onMouseEnter={(e) => {
        const actionsEl = e.currentTarget.querySelector('.sticky-actions') as HTMLElement;
        if (actionsEl) {
          const rowRect = e.currentTarget.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const finalTop = Math.max(20, Math.min(viewportHeight - 60, rowRect.top));
          actionsEl.style.top = `${finalTop}px`;
          actionsEl.style.opacity = '1';
          actionsEl.style.visibility = 'visible';
          actionsEl.style.pointerEvents = 'auto';
        }
      }}
      onMouseLeave={(e) => {
        const actionsEl = e.currentTarget.querySelector('.sticky-actions') as HTMLElement;
        if (actionsEl) {
          actionsEl.style.opacity = '0';
          actionsEl.style.visibility = 'hidden';
          actionsEl.style.pointerEvents = 'none';
        }
      }}
    >
      {isMobile && (
        <TableCell
          className="tw-sticky tw-left-0 tw-z-[15] tw-bg-background !tw-px-1 !tw-py-1 !tw-align-middle"
          style={{
            width: MOBILE_ACTIONS_COLUMN_WIDTH,
            minWidth: MOBILE_ACTIONS_COLUMN_WIDTH,
            maxWidth: MOBILE_ACTIONS_COLUMN_WIDTH,
          }}
        >
          <div className="tw-flex tw-items-center tw-gap-0.5">
            <span className="tw-min-w-4 tw-text-right tw-text-xs tw-tabular-nums tw-text-muted-foreground">
              {rowIndex + 1}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Row ${rowIndex + 1} actions`}
                  className="tw-h-8 tw-w-8 tw-p-0"
                >
                  <EllipsisVerticalIcon className="tw-h-4 tw-w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="tw-z-[1000] tw-w-52">
                <DropdownMenuLabel className="tw-truncate tw-text-xs tw-font-normal tw-text-muted-foreground">
                  {rowLabel}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => ontoggleRowExpand(rowIndex)}>
                  {isExpanded ? (
                    <ChevronRightIcon className="tw-h-4 tw-w-4" />
                  ) : (
                    <ChevronDownIcon className="tw-h-4 tw-w-4" />
                  )}
                  {isExpanded ? 'Collapse row' : 'Expand row'}
                </DropdownMenuItem>
                {onInsertRow && (
                  <DropdownMenuItem onSelect={() => onInsertRow(rowIndex + 1)}>
                    <PlusIcon className="tw-h-4 tw-w-4" />
                    Insert row below
                  </DropdownMenuItem>
                )}
                {onDuplicateRow && (
                  <DropdownMenuItem onSelect={() => onDuplicateRow(rowIndex)}>
                    <DocumentDuplicateIcon className="tw-h-4 tw-w-4" />
                    Duplicate row
                  </DropdownMenuItem>
                )}
                {onDeleteRow && (
                  <DropdownMenuItem
                    variant="destructive"
                    className="tw-text-red-600"
                    onSelect={() => onDeleteRow(rowIndex)}
                  >
                    <TrashIcon className="tw-h-4 tw-w-4" />
                    Delete row
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      )}
      {showInsertColumn && (
        <TableCell
          className="tw-sticky tw-left-0 tw-w-0 tw-p-0 tw-overflow-visible tw-z-[15] group-hover:tw-z-[16]"
          style={{ border: 'none' }}
        >
          {onInsertRow && (
            <button
              type="button"
              title="Insert row here"
              className="tw-absolute tw-bottom-[-11px] tw-left-2 tw-w-[22px] tw-h-[22px] tw-rounded-full tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-border-2 tw-border-white tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-p-0 tw-outline-none tw-opacity-0 group-hover:tw-opacity-100 tw-pointer-events-none group-hover:tw-pointer-events-auto"
              style={{ boxShadow: '0 2px 6px rgba(37,99,235,0.45)' }}
              onClick={(e) => { e.stopPropagation(); onInsertRow(rowIndex + 1); }}
            >
              <PlusIcon className="tw-w-3 tw-h-3 tw-stroke-[2.5] tw-text-white" />
            </button>
          )}
        </TableCell>
      )}
      {columns.map((column: ColumnConfig, colIndex: number) => (
        <TableCell
          key={colIndex}
          onClick={(e) => handleCellClick(e, column)}
          className={`${(column.cell.type === 'editable' && column.cell.editable !== false) || column.cell.type === 'custom' ? 'tw-cursor-pointer' : 'tw-cursor-default'}`}
          style={{ verticalAlign: 'top', maxHeight: isExpanded ? '300px' : 'auto' }}
        >
          <div
            className={isExpanded ? 'tw-max-h-[300px] tw-overflow-y-auto' : ''}
            style={{ maxHeight: isExpanded ? '300px' : 'auto' }}
          >
            {renderCell(column, row, rowIndex)}
          </div>
        </TableCell>
      ))}

      {/* Floating action panel: hover-only, so desktop-only. Phones use the row actions menu above. */}
      {!isMobile && (
        <div
          className="sticky-actions tw-fixed tw-right-4 tw-flex tw-gap-1 tw-opacity-0 tw-invisible tw-z-30 tw-bg-slate-900 tw-border tw-border-slate-700 tw-rounded-lg tw-shadow-xl tw-p-1.5 tw-my-[5px]"
          style={{ pointerEvents: 'none' }}
          onMouseEnter={(e) => {
            const actionsEl = e.currentTarget as HTMLElement;
            actionsEl.classList.remove('tw-opacity-0');
            actionsEl.classList.add('tw-opacity-100');
          }}
        >
          {isResolved && (
            <div className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-text-green-400 tw-border-r tw-border-slate-700 tw-pr-2 tw-mr-0.5 tw-pointer-events-none">
              <CheckCircleIcon className="tw-h-3.5 tw-w-3.5" />
              <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide">Fixed</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => ontoggleRowExpand(rowIndex)}
            title={isExpanded ? 'Collapse row' : 'Expand row'}
            className="tw-h-8 tw-w-8 tw-p-0 tw-text-slate-300 hover:tw-text-white hover:tw-bg-slate-800 tw-transition-colors"
          >
            {isExpanded
              ? <ChevronDownIcon className="tw-h-4 tw-w-4" />
              : <ChevronRightIcon className="tw-h-4 tw-w-4" />
            }
          </Button>
          {onDuplicateRow && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDuplicateRow(rowIndex)}
              title="Duplicate row"
              className="tw-h-8 tw-w-8 tw-p-0 tw-text-slate-300 hover:tw-text-white hover:tw-bg-slate-800 tw-transition-colors"
            >
              <DocumentDuplicateIcon className="tw-h-4 tw-w-4" />
            </Button>
          )}
          {onDeleteRow && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDeleteRow(rowIndex)}
              title="Delete row"
              className="tw-h-8 tw-w-8 tw-p-0 tw-text-red-400 hover:tw-text-red-300 hover:tw-bg-slate-800 tw-transition-colors"
            >
              <TrashIcon className="tw-h-4 tw-w-4" />
            </Button>
          )}
        </div>
      )}
    </ShadcnTableRow>
  );
};

export default PredictionsTableRow;
